
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import CustomModal from '../../components/common/CustomModal';
import { toast } from 'react-toastify';
import { CgArrowLeft } from 'react-icons/cg';
import { FiHelpCircle } from 'react-icons/fi';
import { TbCoins, TbCurrencyPound, TbArrowsExchange, TbPercentage, TbCake } from 'react-icons/tb';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import Layout from '../../components/global/layout';
import shared from './shared';
import { useSelector } from 'react-redux';
import methodModel from '../../methods/methods';
import { FaChevronLeft } from 'react-icons/fa';
import { RiFileList3Line } from 'react-icons/ri';

// ─── Config ────────────────────────────────────────────────────────────────
const SETTINGS_KEY = 'payldo'; // key used in API payload

const DEFAULT_FORM = {
  defaultPoints: '',
  exchange_rate: '',
  min_redemption_percentage: '',
  max_redemption_percentage: '',
  downloadAppRewards: '',
  birthdayRewards: '',
};

// ─── Helpers ────────────────────────────────────────────────────────────────
const toNum = (v) => (v === '' || v === undefined || v === null ? '' : Number(v));

function InputField({
  label,
  sublabel,
  icon: Icon,
  prefix,
  suffix,
  name,
  value,
  onChange,
  min,
  max,
  step = 1,
  error,
  hint,
  placeholder = '0',
}) {
  return (
    <div className="mb-1">
      <label className="mb-1.5 flex items-center gap-1.5 flex-wrap text-sm font-medium text-gray-700">
        {Icon && <Icon size={14} className="text-[#CC9900]" />}
        {label}
        {sublabel && (
          <span className="ml-1 text-[11px] font-normal text-gray-400">{sublabel}</span>
        )}
      </label>

      <div className="relative flex items-center">
        {prefix && (
          <span className="absolute left-3 text-sm font-medium text-gray-400 pointer-events-none select-none">
            {prefix}
          </span>
        )}
        <input
          type="number"
          name={name}
          value={value}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          onChange={(e) => onChange(name, e.target.value)}
          className={[
            'border bg-white w-full text-sm placeholder:text-gray-400 rounded-lg h-10',
            'flex items-center overflow-hidden',
            '!ring-[#CC9900] !outline-[#CC9900]',
            'transition-colors duration-150',
            prefix ? 'pl-8 pr-3' : suffix ? 'pl-3 pr-8' : 'px-3',
            error
              ? 'border-red-400 focus:border-red-500'
              : 'border-gray-200 focus:border-[#CC9900]',
          ].join(' ')}
        />
        {suffix && (
          <span className="absolute right-3 text-sm font-medium text-gray-400 pointer-events-none select-none">
            {suffix}
          </span>
        )}
      </div>

      {hint && !error && (
        <p className="mt-1 text-[11px] text-gray-400 leading-relaxed">{hint}</p>
      )}
      {error && (
        <p className="mt-1 text-[11px] text-red-500 leading-relaxed">{error}</p>
      )}
    </div>
  );
}

// ─── Live preview pill ───────────────────────────────────────────────────────
function PreviewPill({ points, rate }) {
  const p = Number(points) || 0;
  const r = Number(rate) || 0;
  if (!p || !r) return null;
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#CC990012] border border-[#ffe295] text-xs font-medium text-[#CC9900]">
      <TbCoins size={13} />
      1 pts = £{parseFloat(r / p).toFixed(1)}

    </div>
  );
}

// ─── Section card wrapper ────────────────────────────────────────────────────
function Section({ title, description, children }) {
  return (
    <div className="bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border">
      <div className='mb-5'>
        <div className='flex gap-2'>
          <RiFileList3Line className="text-[#CC9900] text-xl relative top-[1px]" />
          <h4 className='text-[#2D2D2D] font-semibold'>{title}</h4>
        </div>
        {description && (
          <p className="text-sm text-[#717182]">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function Settings() {
  const user = useSelector(state => state.user);
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);

  // ── Load current settings on mount ──────────────────────────────────────
  useEffect(() => {
    loader(true);
    ApiClient.get(shared.detailApi).then((res) => {
      if (res?.success && res?.data) {
        const d = res.data;
        console.log(res?.data, "Settings data")
        setForm({
          defaultPoints: toNum(d.defaultPoints),
          exchange_rate: toNum(d.exchange_rate),
          min_redemption_percentage: toNum(d.min_redemption_percentage),
          max_redemption_percentage: toNum(d.max_redemption_percentage),
          downloadAppRewards: toNum(d.downloadAppRewards),
          birthdayRewards: toNum(d.birthdayRewards),
        });
      }
      loader(false);
    });
  }, []);

  // ── Field change ─────────────────────────────────────────────────────────
  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    if (saveSuccess) setSaveSuccess(false);
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const errors = {};

  if (submitted) {
    if (form.defaultPoints === '' || form.defaultPoints <= 0)
      errors.defaultPoints = 'Points must be a positive number.';

    if (form.exchange_rate === '' || form.exchange_rate <= 0)
      errors.exchange_rate = 'Exchange rate must be a positive number.';

    if (form.min_redemption_percentage === '' || form.min_redemption_percentage < 0)
      errors.min_redemption_percentage = 'Min percentage must be 0 or more.';
    else if (form.min_redemption_percentage > 100)
      errors.min_redemption_percentage = 'Min percentage cannot exceed 100%.';

    if (form.max_redemption_percentage === '' || form.max_redemption_percentage <= 0)
      errors.max_redemption_percentage = 'Max percentage must be a positive number.';
    else if (form.max_redemption_percentage > 100)
      errors.max_redemption_percentage = 'Max percentage cannot exceed 100%.';

    if (
      !errors.min_redemption_percentage &&
      !errors.max_redemption_percentage &&
      Number(form.min_redemption_percentage) >= Number(form.max_redemption_percentage)
    ) {
      errors.max_redemption_percentage = 'Max must be greater than min percentage.';
    }

    if (form.downloadAppRewards === '' || form.downloadAppRewards < 0)
      errors.downloadAppRewards = 'Rewards must be 0 or more.';

    if (form.birthdayRewards === '' || form.birthdayRewards < 0)
      errors.birthdayRewards = 'Birthday rewards must be 0 or more.';
  }

  const isValid = Object.keys(errors).length === 0;

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    // Re-run validation without relying on state flush
    const hasError =
      !form.defaultPoints || form.defaultPoints <= 0 ||
      !form.exchange_rate || form.exchange_rate <= 0 ||
      form.min_redemption_percentage === '' || form.min_redemption_percentage < 0 ||
      form.max_redemption_percentage === '' || form.max_redemption_percentage <= 0 ||
      Number(form.min_redemption_percentage) >= Number(form.max_redemption_percentage) ||
      form.downloadAppRewards === '' || form.downloadAppRewards < 0 ||
      form.birthdayRewards === '' || form.birthdayRewards < 0;

    if (hasError) return;

    const payload = {
      defaultPoints: Number(form.defaultPoints),
      exchange_rate: Number(form.exchange_rate),
      min_redemption_percentage: Number(form.min_redemption_percentage),
      max_redemption_percentage: Number(form.max_redemption_percentage),
      downloadAppRewards: Number(form.downloadAppRewards),
      birthdayRewards: Number(form.birthdayRewards),
    };

    setSaving(true);
    loader(true);
    ApiClient.post(shared.addApi, payload).then((res) => {
      if (res?.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
      setSaving(false);
      loader(false);
    });
  };

  return (
    <Layout>
      <form onSubmit={handleSubmit} style={{ pointerEvents: methodModel.hasPermission(user, shared.permissionKey, 'edit') ? '' : 'none' }}>
        <div className="pprofile1">

          <div className="flex justify-between items-center gap-3 mb-8 flex-wrap">
            <div>
              <h3 className="text-lg lg:text-2xl font-semibold text-[#221E10]">
                Points Settings
              </h3>
              <p className='text-sm text-[#2D2D2D]'> Configure how points are earned, valued, and redeemed.</p>
            </div>
            {methodModel.hasPermission(user, shared.permissionKey, 'edit') ? (
              <button
                type="submit"
                disabled={saving}
                className={[
                  'leading-10 h-10 flex items-center shadow-btn px-6 hover:opacity-80 text-sm text-white rounded-full gap-1',
                  'focus:ring-0 focus:outline-none',
                  'transition-all duration-200',
                  saveSuccess
                    ? 'bg-green-500 text-white'
                    : 'bg-primary text-white',
                  saving ? 'opacity-60 cursor-not-allowed' : '',
                ].join(' ')}
              >
                {saving ? 'Saving…' : saveSuccess ? '✓ Saved' : 'Save settings'}
              </button>
            ) : null}
          </div>

          <div className='grid grid-cols-1 xl:grid-cols-2 gap-6'>
            <Section
              title="Exchange rate"
              description="Define how many points equal 1 pound sterling."
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <InputField
                  label="Points"
                  icon={TbCoins}
                  name="defaultPoints"
                  value={form.defaultPoints}
                  onChange={handleChange}
                  min={1}
                  placeholder="e.g. 5"
                  hint="Number of points the customer earns or spends."
                  error={errors.defaultPoints}
                />
                <InputField
                  label="Pound value"
                  sublabel="(exchange rate)"
                  icon={TbCurrencyPound}
                  prefix="£"
                  name="exchange_rate"
                  value={form.exchange_rate}
                  onChange={handleChange}
                  placeholder="e.g. 1"
                  hint="Pound amount these points are worth."
                  error={errors.exchange_rate}
                />
              </div>

              {/* Live preview */}
              <div className="mt-3 flex items-center gap-2">
                <TbArrowsExchange size={14} className="text-gray-300" />
                <span className="text-[12px] text-gray-400">Live preview:</span>
                <PreviewPill points={form.defaultPoints} rate={form.exchange_rate} />
                {(!form.defaultPoints || !form.exchange_rate) && (
                  <span className="text-[12px] text-gray-300 italic">fill both fields to preview</span>
                )}
              </div>
            </Section>


            <Section
              title="Redemption limits"
              description="Set the minimum and maximum percentage of an order a customer can pay using points."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Min redemption"
                  icon={TbPercentage}
                  suffix="%"
                  name="min_redemption_percentage"
                  value={form.min_redemption_percentage}
                  onChange={handleChange}
                  min={0}
                  max={100}
                  placeholder="e.g. 5"
                  hint="Minimum % of order payable with points."
                  error={errors.min_redemption_percentage}
                />
                <InputField
                  label="Max redemption"
                  icon={TbPercentage}
                  suffix="%"
                  name="max_redemption_percentage"
                  value={form.max_redemption_percentage}
                  onChange={handleChange}
                  min={1}
                  max={100}
                  placeholder="e.g. 50"
                  hint="Maximum % of order payable with points."
                  error={errors.max_redemption_percentage}
                />
              </div>

              {/* Range visual */}
              {form.min_redemption_percentage !== '' && form.max_redemption_percentage !== '' && (
                <div className="mt-4 px-1">
                  <div className="flex justify-between text-[11px] text-gray-400 mb-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                  <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="absolute h-full bg-[#CC9900] rounded-full transition-all duration-300"
                      style={{
                        left: `${Math.min(Math.max(Number(form.min_redemption_percentage), 0), 100)}%`,
                        width: `${Math.max(
                          Math.min(Number(form.max_redemption_percentage), 100) -
                          Math.min(Math.max(Number(form.min_redemption_percentage), 0), 100),
                          0
                        )}%`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] font-medium text-[#CC9900] mt-1">
                    <span>{form.min_redemption_percentage}% min</span>
                    <span>{form.max_redemption_percentage}% max</span>
                  </div>
                </div>
              )}
            </Section>

            <Section
              title="Bonus Rewards"
              description="Configure one-off points for specific customer actions."
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <InputField
                  label="App Download Reward"
                  icon={TbCoins}
                  name="downloadAppRewards"
                  value={form.downloadAppRewards}
                  onChange={handleChange}
                  min={0}
                  placeholder="e.g. 50"
                  hint="Points awarded when a customer downloads the app for the first time."
                  error={errors.downloadAppRewards}
                />
                <InputField
                  label="Birthday Reward"
                  icon={TbCake}
                  name="birthdayRewards"
                  value={form.birthdayRewards}
                  onChange={handleChange}
                  min={0}
                  placeholder="e.g. 100"
                  hint="Points automatically credited to the user's account on their birthday."
                  error={errors.birthdayRewards}
                />
              </div>
            </Section>
          </div>




        </div>
      </form>

      {/* How It Works Guide Modal */}
      <CustomModal
        open={isHelpModalVisible}
        onClose={() => setIsHelpModalVisible(false)}
        width={650}
      >
        <div className="flex flex-col font-sans">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#CC9900] via-[#E6B800] to-[#CC9900] p-6 text-white relative">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-xl font-bold leading-snug">
                  💡 How Points & Rewards Settings Work
                </h3>
                <p className="text-sm text-white/90 mt-1">
                  Understanding global settings, venue inheritance, and bonus rewards
                </p>
              </div>
            </div>
          </div>

          {/* Ticket Dotted Separator */}
          <div className="relative bg-gray-50" style={{ height: '28px' }}>
            <svg className="absolute left-0 top-0 h-full" style={{ width: '14px' }} viewBox="0 0 14 28" preserveAspectRatio="none">
              <path d="M0,0 C14,0 14,28 0,28 Z" fill="white" />
            </svg>
            <div className="absolute" style={{ top: '50%', left: '14px', right: '14px', transform: 'translateY(-50%)', borderTop: '1.5px dashed #d1d5db' }} />
            <svg className="absolute right-0 top-0 h-full" style={{ width: '14px' }} viewBox="0 0 14 28" preserveAspectRatio="none">
              <path d="M14,0 C0,0 0,28 14,28 Z" fill="white" />
            </svg>
          </div>

          {/* Modal Body */}
          <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600 leading-relaxed">
              These configurations set the default values for points calculations and client engagement rules across the TavLoy platform.
            </p>

            <div className="space-y-4">
              {/* Global Defaults & Venue Patching */}
              <div className="flex gap-4 p-4 rounded-xl border border-[#CC9900]/20 bg-[#CC9900]/5">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#CC9900]/10 text-[#CC9900] flex-shrink-0">
                  <TbArrowsExchange size={18} />
                </span>
                <div>
                  <h4 className="font-bold text-[#8B6E1E] text-sm mb-1">Global Default Settings & Venue Overrides</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    The **Exchange Rate** and **Redemption Limits** configured here act as system-wide defaults that automatically apply to all venues. However, **venues can customize these rules** individually for their location by updating their venue profile settings.
                  </p>
                </div>
              </div>

              {/* Bonus Rewards */}
              <div className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-200/50 text-gray-500 flex-shrink-0">
                  <TbCoins size={18} />
                </span>
                <div>
                  <h4 className="font-bold text-gray-700 text-sm mb-1">Bonus Rewards (App Downloads)</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    When a guest user scans a venue's QR code and downloads the TavLoy app, they are awarded the **App Download Reward** points to incentivize signup and onboard them into the loyalty program.
                  </p>
                </div>
              </div>

              {/* Birthday Rewards */}
              <div className="flex gap-4 p-4 rounded-xl border border-amber-100 bg-amber-50/50">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex-shrink-0">
                  <TbCake size={18} />
                </span>
                <div>
                  <h4 className="font-bold text-[#8B6E1E] text-sm mb-1">Birthday Rewards</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Registered customers automatically receive the configured **Birthday Reward** points on their birthday to drive delight and reward customer loyalty.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end">
            <button
              onClick={() => setIsHelpModalVisible(false)}
              className="px-5 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all text-xs font-semibold"
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      </CustomModal>

      {/* Floating Help Button */}
      <Tooltip placement="left" title="How It Works">
        <button
          type="button"
          onClick={() => setIsHelpModalVisible(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-[#CC9900] text-white shadow-lg hover:bg-[#B38000] hover:scale-110 active:scale-95 transition-all duration-300"
        >
          <FiHelpCircle className="text-2xl" />
        </button>
      </Tooltip>
    </Layout>
  );
}